import { useEffect, useRef, useState } from 'react';
import type { Map as MapLibreMap, Marker as MapLibreMarker } from 'maplibre-gl';
import type { Coordinates } from './delivery-location';
import { configuredMapStyle } from './map-style';
import { reportMapDiagnostic } from './map-diagnostics';

interface Props {
  readonly center: Coordinates;
  readonly zoom?: number;
  readonly point?: Coordinates;
  readonly provider: 'NONE' | 'OPENFREEMAP' | 'CUSTOM_STYLE';
  readonly styleUrl?: string;
  readonly onChange: (latitude: number, longitude: number) => void;
}

export function DeliveryMap({ center, zoom = 15, point, provider, styleUrl, onChange }: Props) {
  const container = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const [partial, setPartial] = useState(false);
  const callback = useRef(onChange);
  const selectedPoint = useRef(point);
  const mapInstance = useRef<MapLibreMap | null>(null);
  const markerInstance = useRef<MapLibreMarker | null>(null);
  callback.current = onChange;
  selectedPoint.current = point;
  const mapStyle = configuredMapStyle(
    provider,
    styleUrl,
    import.meta.env.VITE_CUSTOMER_MAP_STYLE_URL
  );
  const initialCenter = useRef(center);
  const initialZoom = useRef(zoom);

  useEffect(() => {
    if (!container.current || !mapStyle) return;
    container.current.focus();
    container.current.scrollIntoView({ block: 'nearest' });
    let disposed = false;
    let hasLoaded = false;
    let cleanup: () => void = () => undefined;
    void import('maplibre-gl')
      .then(({ Map, Marker, NavigationControl }) => {
        if (disposed || !container.current) return;
        const latestCenter = selectedPoint.current ?? initialCenter.current;
        const map = new Map({
          container: container.current,
          style: mapStyle,
          center: [latestCenter.longitude, latestCenter.latitude],
          zoom: selectedPoint.current ? 15 : initialZoom.current,
          locale: {
            'NavigationControl.ZoomIn': 'Aproximar',
            'NavigationControl.ZoomOut': 'Afastar',
            'AttributionControl.ToggleAttribution': 'Mostrar atribuição',
            'Map.Title': 'Mapa do ponto de entrega'
          },
          attributionControl: {}
        });
        mapInstance.current = map;
        map.addControl(new NavigationControl({ showCompass: false }), 'top-right');
        for (const event of [
          'style.load',
          'sourcedata',
          'webglcontextlost',
          'webglcontextrestored'
        ] as const)
          map.on(event, () => reportMapDiagnostic(map, event));
        map.resize();
        const observer =
          typeof ResizeObserver === 'undefined'
            ? undefined
            : new ResizeObserver(() => {
                if (!disposed) map.resize();
              });
        observer?.observe(container.current);
        map.on('error', () => {
          reportMapDiagnostic(map, 'error');
          if (disposed) return;
          if (hasLoaded) setPartial(true);
          else setFailed(true);
        });
        map.on('load', () => {
          reportMapDiagnostic(map, 'load');
          hasLoaded = true;
          if (!disposed) map.resize();
          if (!disposed) setFailed(false);
        });
        map.on('idle', () => {
          reportMapDiagnostic(map, 'idle');
          if (!disposed && map.areTilesLoaded() && map.isStyleLoaded()) {
            setFailed(false);
            setPartial(false);
          }
        });
        const marker = new Marker({ draggable: true });
        markerInstance.current = marker;
        const initialPoint = selectedPoint.current;
        if (initialPoint)
          marker.setLngLat([initialPoint.longitude, initialPoint.latitude]).addTo(map);
        const selectPoint = (longitude: number, latitude: number): void => {
          marker.setLngLat([longitude, latitude]);
          if (!marker.getElement().isConnected) marker.addTo(map);
          callback.current(latitude, longitude);
        };
        map.on('click', (event) => selectPoint(event.lngLat.lng, event.lngLat.lat));
        marker.on('dragend', () => {
          const selected = marker.getLngLat();
          callback.current(selected.lat, selected.lng);
        });
        cleanup = () => {
          observer?.disconnect();
          marker.remove();
          markerInstance.current = null;
          mapInstance.current = null;
          map.remove();
        };
      })
      .catch(() => {
        if (!disposed) setFailed(true);
      });
    return () => {
      disposed = true;
      cleanup();
    };
  }, [mapStyle]);

  useEffect(() => {
    const marker = markerInstance.current;
    const map = mapInstance.current;
    if (!point || !marker || !map) return;
    marker.setLngLat([point.longitude, point.latitude]);
    if (!marker.getElement().isConnected) marker.addTo(map);
    map.easeTo({ center: [point.longitude, point.latitude], zoom: Math.max(map.getZoom(), 15) });
  }, [point?.latitude, point?.longitude]);

  if (!mapStyle)
    return (
      <div className="map-fallback" role="status">
        O mapa base não está configurado. Use sua localização atual ou escolha Retirada; o restante
        do cardápio continua disponível.
      </div>
    );
  return (
    <>
      {partial && !failed ? (
        <p role="status">
          Alguns detalhes do mapa estão temporariamente indisponíveis. Confira o ponto selecionado.
        </p>
      ) : null}
      {failed ? (
        <p role="status">
          Não foi possível carregar o mapa completo. Seu endereço e ponto selecionado foram
          preservados. Tente novamente ou use sua localização atual.
        </p>
      ) : null}
      <div
        className="delivery-map"
        ref={container}
        tabIndex={-1}
        aria-label="Mapa do ponto de entrega"
      />
    </>
  );
}
